import React, { useState, useRef, useEffect } from "react"
import { ALL_STATUSES } from "@features/applicationsManager/ApplicationCard/statusDefinitions"
import { MultiSelectDropdown } from "./MultiSelectDropdown"
import {
  PanelContainer,
  PanelHeader,
  SectionTitle,
  Row,
  Label,
  InputField,
  NumericInput,
  DateInput,
  SelectField,
  MultiSelectContainer,
  Divider,
  Actions,
  ResetButton,
  TriggerButton,
} from "./Style"

// Default: exclude "Discarté" from the default selection.
const defaultStatusSelection = ALL_STATUSES.filter((s) => s.label !== "Discarté").map((s) => s.label)

const containerRef = React.createRef<HTMLDivElement>()

const FilterIconSVG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4h18l-7 8v6l-4 2v-8l-7-8z" />
  </svg>
)

export interface FilterDropdownProps {
  onApply: (filters: Record<string, any>) => void
  onReset: () => void
  isMobile?: boolean
  recentFilters?: string[]
  statistics?: string
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ onApply, onReset, isMobile = false, recentFilters, statistics }) => {
  // Consolidate all filters in one object.
  const [filters, setFilters] = useState({
    globalSearch: "",
    minOccupants: "",
    maxOccupants: "",
    animaux: "",
    moveInFrom: "",
    moveInTo: "",
    submittedFrom: "",
    submittedTo: "",
    apartmentAddress: "",
    proposeApartment: false,
    desiredSize: "",
    quebecResidenceDuration: "",
    comfortableWithCreditCheck: false,
    hasGuarantor: false,
    visitAvailability: "",
    status: defaultStatusSelection,
  })

  const containerRefLocal = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const handleToggleOpen = () => setOpen((prev) => !prev)

  // Update a filter field and update the filters state.
  const updateFilter = (key: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  // Call onApply only when filters actually change (using JSON.stringify for deep comparison)
  useEffect(() => {
    onApply(filters)
  }, [JSON.stringify(filters), onApply])

  const handleReset = () => {
    const resetFilters = {
      globalSearch: "",
      minOccupants: "",
      maxOccupants: "",
      animaux: "",
      moveInFrom: "",
      moveInTo: "",
      submittedFrom: "",
      submittedTo: "",
      apartmentAddress: "",
      proposeApartment: false,
      desiredSize: "",
      quebecResidenceDuration: "",
      comfortableWithCreditCheck: false,
      hasGuarantor: false,
      visitAvailability: "",
      status: defaultStatusSelection,
    }
    setFilters(resetFilters)
    onReset()
  }

  // Close panel if clicking outside (both mobile and desktop)
  const handleClickOutside = (e: MouseEvent) => {
    if (containerRefLocal.current && !containerRefLocal.current.contains(e.target as Node)) {
      // Check if the click occurred in an element that is part of a multi-select portal.
      if ((e.target as HTMLElement).closest('[data-portal="multiselect"]')) {
        // Do nothing if the click is inside the portal.
        return
      }

      setOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Build options for multi-select from ALL_STATUSES
  const statusOptions = ALL_STATUSES.map((s) => ({
    value: s.label,
    label: `${s.label} (${s.phase})`,
  }))

  return (
    <div ref={containerRefLocal} style={{ position: "relative", display: "flex" }}>
      <TriggerButton onClick={handleToggleOpen} aria-haspopup="true">
        <FilterIconSVG /> Filtres
      </TriggerButton>
      {open && (
        <PanelContainer>
          <PanelHeader>
            <InputField
              placeholder="Rechercher..."
              value={filters.globalSearch}
              onChange={(e) => updateFilter("globalSearch", e.target.value)}
              aria-label="Rechercher un filtre"
            />
          </PanelHeader>
          <SectionTitle>Filtres:</SectionTitle>
          <div>
            <Row>
              <Label>Occupants min:</Label>
              <NumericInput type="number" value={filters.minOccupants} onChange={(e) => updateFilter("minOccupants", e.target.value)} />
              <Label>max:</Label>
              <NumericInput type="number" value={filters.maxOccupants} onChange={(e) => updateFilter("maxOccupants", e.target.value)} />
            </Row>
            <Row>
              <Label>Animaux:</Label>
              <InputField
                type="text"
                placeholder="ex: chien, chat"
                value={filters.animaux}
                onChange={(e) => updateFilter("animaux", e.target.value)}
              />
            </Row>
            <Row>
              <Label>Prise de possession (de):</Label>
              <DateInput type="date" value={filters.moveInFrom} onChange={(e) => updateFilter("moveInFrom", e.target.value)} />
              <Label>à:</Label>
              <DateInput type="date" value={filters.moveInTo} onChange={(e) => updateFilter("moveInTo", e.target.value)} />
            </Row>
            <Row>
              <Label>Date réception candidature (de):</Label>
              <DateInput type="date" value={filters.submittedFrom} onChange={(e) => updateFilter("submittedFrom", e.target.value)} />
              <Label>à:</Label>
              <DateInput type="date" value={filters.submittedTo} onChange={(e) => updateFilter("submittedTo", e.target.value)} />
            </Row>
            <Row>
              <Label>Adresse:</Label>
              <InputField
                type="text"
                placeholder="Adresse"
                value={filters.apartmentAddress}
                onChange={(e) => updateFilter("apartmentAddress", e.target.value)}
              />
            </Row>
            <Row>
              <Label>Propose appartement:</Label>
              <input type="checkbox" checked={filters.proposeApartment} onChange={(e) => updateFilter("proposeApartment", e.target.checked)} />
            </Row>
            <Row>
              <Label>Grandeur désirée:</Label>
              <InputField
                type="text"
                placeholder="Grandeur désirée"
                value={filters.desiredSize}
                onChange={(e) => updateFilter("desiredSize", e.target.value)}
              />
            </Row>
            <Row>
              <Label>Québec depuis:</Label>
              <SelectField value={filters.quebecResidenceDuration} onChange={(e) => updateFilter("quebecResidenceDuration", e.target.value)}>
                <option value="">-- Choisir --</option>
                <option value="plus d'un an">plus d'un an</option>
                <option value="moins d'un an">moins d'un an</option>
              </SelectField>
            </Row>
            <Row>
              <Label>Confortable avec enquête de crédit</Label>
              <input
                type="checkbox"
                checked={filters.comfortableWithCreditCheck}
                onChange={(e) => updateFilter("comfortableWithCreditCheck", e.target.checked)}
              />
            </Row>
            <Row>
              <Label>A un garant:</Label>
              <input type="checkbox" checked={filters.hasGuarantor} onChange={(e) => updateFilter("hasGuarantor", e.target.checked)} />
            </Row>
            <Row>
              <Label>Disponibilités pour visites</Label>
              <InputField
                type="text"
                placeholder="ex: fin de semaine en pm"
                value={filters.visitAvailability}
                onChange={(e) => updateFilter("visitAvailability", e.target.value)}
              />
            </Row>
            <Row>
              <Label>Status:</Label>
              <MultiSelectContainer>
                <MultiSelectDropdown
                  options={statusOptions}
                  selected={filters.status}
                  onChange={(selected) => updateFilter("status", selected)}
                  placeholder="Sélectionner..."
                />
              </MultiSelectContainer>
            </Row>
          </div>
          <Divider />
          <Actions>
            <ResetButton onClick={handleReset}>Réinitialiser</ResetButton>
          </Actions>
        </PanelContainer>
      )}
    </div>
  )
}

export default FilterDropdown
